import { useContext, useState, useEffect } from "react";
import { cookiePolicyLink, cryptoSvgs, getCurrencySvg, PATHS, RAMPING } from "utils/consts";

import Button from "../common/buttons/Button";
import Input from "../common/inputs/Input";
import SelectButton from "../common/buttons/SelectButton";
import { SelectCryptoPopup, SelectCurrencyPopup } from "../common/popups";
import { useNavigate, useSearchParams } from "react-router-dom";
import BuyCryptoSummaryV2 from "components/common/BuyCryptoSummaryV2";
import { useDispatch, useSelector } from "react-redux";
import { AppActions, RootState } from "store";
import { PlatformContext } from "contexts";
import { GetQuoteInput, QuoteDirection, TransactionFeeType, TransactionTypeForFee } from "types";
import { SelectFeeType } from "components/common/selects";
import { quoteApi } from "api/quote.api";
import { EventNames } from "utils/gtm";
import { useGoogleAnalytics } from "hooks";

interface BuyCryptoV2Props {
  currency?: string;
  crypto?: string;
  fiatAmount?: number;
}

const BuyCryptoV2: React.FC<BuyCryptoV2Props> = ({ currency: _currency, crypto: _crypto, fiatAmount: _fiatAmount }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [params] = useSearchParams();

  const referralCode = params.get('promoCode') || '';

  const { saveQuoteInput, getQuoteInput, isLoggedIn, saveFeeIncludeType, getFeeIncludeType } = useContext(PlatformContext);
  const { sendEvent } = useGoogleAnalytics();
  
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { quoteResult } = useSelector((state: RootState) => state.quoteV2);
  
  const [fiatAmount, setFiatAmount] = useState<number>(getQuoteInput(TransactionTypeForFee.RETAIL_ON_RAMP).fromAmount || 100);
  const [currency, setCurrency] = useState<string>(getQuoteInput(TransactionTypeForFee.RETAIL_ON_RAMP).from || 'EUR');
  const [isFiatOpen, setFiatOpen] = useState<boolean>(false);
  
  const [cryptoAmount, setCryptoAmount] = useState<number>(getQuoteInput(TransactionTypeForFee.RETAIL_ON_RAMP).toAmount || 0);
  const [crypto, setCrypto] = useState<string>(getQuoteInput(TransactionTypeForFee.RETAIL_ON_RAMP).to || 'USDC');
  const [isCryptoOpen, setCryptoOpen] = useState<boolean>(false);
  
  const [include, setInclude] = useState<boolean>(getFeeIncludeType());
  
  const [minCryptoLimit, setMinCryptoLimit] = useState(0);
  const [maxCryptoLimit, setMaxCryptoLimit] = useState(0);

  const [isLoading, setLoading] = useState(false);

  const [quoteDirection, setQuoteDirection] = useState<QuoteDirection>(QuoteDirection.FROM);

  const [quoteInputData, setQuoteInputData] = useState<GetQuoteInput | null>(null);

  useEffect(() => {
    if (_currency) {
      setCurrency(_currency);
    }
  }, [_currency]);

  useEffect(() => {
    if (_crypto) {
      setCrypto(_crypto);
    }
  }, [_crypto]);

  useEffect(() => {
    if (_fiatAmount) {
      setFiatAmount(_fiatAmount);
      onChangeFiatAmount(_fiatAmount + '');
    }
  }, [_fiatAmount]);

  useEffect(() => {
    if(!currency || !crypto) return;

    refreshQuote();
  }, [currency, crypto, quoteDirection, include]);

  useEffect(() => {
    if (crypto) {
      setLoading(true);

      quoteApi.getQuoteWithoutFee({
        from: 'EUR',
        to: crypto,
        toAmount: 0,
        transactionType: TransactionTypeForFee.RETAIL_ON_RAMP,
      }).then((data) => {
        setMinCryptoLimit(data.toAmount)
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setLoading(false);
      });

      quoteApi.getQuoteWithoutFee({
        from: 'EUR',
        to: crypto,
        fromAmount: 350,
        transactionType: TransactionTypeForFee.RETAIL_ON_RAMP,
      }).then((data) => {
        setMaxCryptoLimit(data.toAmount);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setLoading(false);
      });
    }
  }, [crypto, isLoggedIn]);

  const getQuote = (quoteData: GetQuoteInput, direction: QuoteDirection) => {
    setQuoteInputData(quoteData);

    if(!isLoggedIn) {
      dispatch(AppActions.quoteV2.getQuoteWithoutFee({
        input: quoteData,
        direction: direction,
        next: (result, direction) => {
          if (direction === QuoteDirection.FROM) {
            setFiatAmount(Number(result.fromAmount.toFixed(2)));
            setCryptoAmount(Number(result.toAmount.toFixed(5)));
          } else {
            setFiatAmount(Number(result.fromAmount.toFixed(2)));
            setCryptoAmount(Number(result.toAmount.toFixed(5)));
          }
        }
      }));
    } else {
      dispatch(AppActions.quoteV2.getQuoteWithFee({
        input: {
          ...quoteData,
          userId: currentUser.id,
          feeType: include ? TransactionFeeType.INCLUDE : TransactionFeeType.EXCLUDE,
        },
        direction: direction,
        next: (result, direction) => {
          if (direction === QuoteDirection.FROM) {
            setFiatAmount(Number(result.fromAmount.toFixed(2)));
            setCryptoAmount(Number(result.toAmount.toFixed(5)));
          } else {
            setFiatAmount(Number(result.fromAmount.toFixed(2)));
            setCryptoAmount(Number(result.toAmount.toFixed(5)));
          }
        }
      }));
    }
  }

  const onChangeFiatAmount = (value: string) => {
    const newFiatAmount = parseFloat(value);

    setFiatAmount(newFiatAmount);
    if(quoteDirection !== QuoteDirection.FROM) {
      setQuoteDirection(QuoteDirection.FROM);
    }

    getQuote({
      from: currency,
      to: crypto,
      fromAmount: newFiatAmount,
      transactionType: TransactionTypeForFee.RETAIL_ON_RAMP,
    }, QuoteDirection.FROM);
  }

  const onChangeCryptoAmount = (value: string) => {
    const newCryptoAmount = parseFloat(value);

    setCryptoAmount(newCryptoAmount);
    if(quoteDirection !== QuoteDirection.TO) {
      setQuoteDirection(QuoteDirection.TO);
    }

    getQuote({
      from: currency,
      to: crypto,
      toAmount: newCryptoAmount,
      transactionType: TransactionTypeForFee.RETAIL_ON_RAMP,
    }, QuoteDirection.TO);
  }

  const onFeeIncludeTypeChange = (newFeeIncludeType: boolean) => {
    setInclude(newFeeIncludeType);
    saveFeeIncludeType(newFeeIncludeType);
  }

  const refreshQuote = () => {
    const quoteData: GetQuoteInput = {
      from: currency,
      to: crypto,
      ...(quoteDirection === QuoteDirection.FROM && {
        fromAmount: Number(fiatAmount),
      }),
      ...(quoteDirection === QuoteDirection.TO && {
        toAmount: Number(cryptoAmount),
      }),
      transactionType: TransactionTypeForFee.RETAIL_ON_RAMP,
    }
    getQuote(quoteData, quoteDirection);
  }

  const onClickContinue = () => {
    if (quoteInputData)
      saveQuoteInput(quoteInputData);
      sendEvent(EventNames.BEGIN_ORDER, {
        orderType: 'buy',
        from: currency,
        to: crypto,
        fromAmount: fiatAmount,
        toAmount: cryptoAmount,
      });
    navigate(`${PATHS.BUY}${PATHS.WALLET_ADDRESS}${referralCode.length ? '?promoCode='+referralCode : ''}`);
  }

  return (
    <div className="flex flex-col justify-between h-full pl-4 pr-4 pt-8 pb-4">
      <div>
        <label className="text-base font-medium text-font-primary">Spend</label>
        <div className="flex mt-2 mb-8">
          <Input
            className="w-full mr-2"
            type="number"
            label={"Amount"}
            value={fiatAmount.toString()}
            onChange={onChangeFiatAmount}
          />
          <SelectButton
            className="w-60"
            text={currency}
            svgPath={getCurrencySvg(currency)}
            label={"Currency"}
            onClick={() => { setFiatOpen(true); }}
          />
        </div>
        <label className="text-base font-medium text-font-primary">Receive</label>
        <div className="flex mt-2 mb-8">
          <Input
            className="w-full mr-2"
            type="number"
            label={"Amount"}
            value={cryptoAmount.toString()}
            onChange={onChangeCryptoAmount}
          />
          <SelectButton
            className="w-60"
            text={crypto}
            svgPath={cryptoSvgs[crypto]}
            label={"Crypto"}
            onClick={() => { setCryptoOpen(true); }}
          />
        </div>

        <SelectFeeType className="mb-4" value={include ? 'include' : 'exclude'} onSelectChange={onFeeIncludeTypeChange} />

        <BuyCryptoSummaryV2
          type={RAMPING.ON}
          refresh={refreshQuote}
        />
      </div>

      <div className="flex flex-col items-center w-full mt-4">
        {
          +cryptoAmount > maxCryptoLimit && +cryptoAmount !=0 && !isLoading &&
          <p className="text-sm font-medium text-feedback-warning mb-2">Exceeds maximum order limit, please try a smaller amount.</p>
        }
        {
          +cryptoAmount < minCryptoLimit && +cryptoAmount !=0 && !isLoading &&
          <p className="text-sm font-medium text-feedback-warning mb-2">Under minimum order limit, please try a larger amount.</p>
        }
        <Button
          className="w-full"
          disabled={
            cryptoAmount == 0 ||
            fiatAmount == 0 ||
            fiatAmount < quoteResult.feeAmount ||
            !(+cryptoAmount >= minCryptoLimit && +cryptoAmount <= maxCryptoLimit)
          }
          onClick={onClickContinue}
        >
          Continue
        </Button>
        <span className="text-xs font-medium text-font-primary mt-2">By continuing you agree to our <a className="underline underline-offset-2" href={cookiePolicyLink} target="_blank">cookie policy</a>.</span>
      </div>
      {
        isCryptoOpen &&
        <SelectCryptoPopup
          type={0}
          crypto={crypto}
          onChange={crypto => setCrypto(crypto)}
          onClose={() => { setCryptoOpen(false); }}
        />
      }
      {
        isFiatOpen &&
        <SelectCurrencyPopup
          type={0}
          currency={currency}
          onChange={currency => setCurrency(currency)}
          onClose={() => { setFiatOpen(false); }}
        />
      }
    </div>
  );
};

export default BuyCryptoV2;
